import React from 'react';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { meterToAny } from 'src/utils/useful_functions';

export type TddSyncLink = {
  id: string;
  name: string;
  product: string;
  path_length: number;
  bandwidth: string;
  license: string;
  aggregate_throughput: string;
  sync_module: string;
  burst_duration_p1: string;
  actual_burst_duration_p1: string;
  burst_duration_p2: string;
  actual_burst_duration_p2: string;
  frame_duration: string;
  e1t1_latency: string;
  phase_1_end: string;
  phase_2_end: string;
  slave_rx_tx_gap: string;
  tdd_frame_offset: string;
  symmetry: string;
  link_ok: boolean;

  // Used by table grouping/selection internals
  path_index: number;
  showSelectionColumn: boolean;
};

// Module scope: createColumnHelper() holds no per-render state, so there is no
// reason to rebuild it on every call (see useSiteColumns).
const columnHelper = createColumnHelper<TddSyncLink>();

export const getTddSyncColumns = (
  rangeUnits: string,
  prefs: any
): ColumnDef<TddSyncLink>[] => {
  return [
    columnHelper.accessor('name', {
      id: 'name',
      header: 'Name',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      cell: ({ row }) => (
        <Link to={`/ptp/${row.original.id}`}>{row.original.name}</Link>
      ),
      size: 250,
    }),
    columnHelper.accessor('product', {
      id: 'product',
      header: 'Product',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      size: 100,
    }),
    columnHelper.accessor('path_length', {
      id: 'path_length',
      header: `Path Length (${rangeUnits})`,
      aggregationFn: 'firstChild',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return meterToAny(value, rangeUnits, 3, false).toFixed(3);
      },
      meta: { prefs, tdStyle: { textAlign: 'right' } },
      size: 70,
    }),
    columnHelper.accessor('bandwidth', {
      id: 'bandwidth',
      header: 'Bandwidth (MHz)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 60,
    }),
    columnHelper.accessor('license', {
      id: 'license',
      header: 'License',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      size: 85,
    }),
    columnHelper.accessor('aggregate_throughput', {
      id: 'aggregate_throughput',
      header: 'Aggregate Throughput (Mbps)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value || value === '') return 'N/A';
        const numValue = Number(value);
        return isNaN(numValue) ? value : numValue.toFixed(3);
      },
      meta: { tdStyle: { textAlign: 'right' } },
      size: 70,
    }),
    columnHelper.accessor('sync_module', {
      id: 'sync_module',
      header: 'Sync Module',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      size: 80,
    }),
    columnHelper.accessor('burst_duration_p1', {
      id: 'burst_duration_p1',
      header: 'Burst Duration P1 (μs)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 60,
    }),
    columnHelper.accessor('actual_burst_duration_p1', {
      id: 'actual_burst_duration_p1',
      header: 'Actual Burst Duration P1 (μs)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 60,
    }),
    columnHelper.accessor('burst_duration_p2', {
      id: 'burst_duration_p2',
      header: 'Burst Duration P2 (μs)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 60,
    }),
    columnHelper.accessor('actual_burst_duration_p2', {
      id: 'actual_burst_duration_p2',
      header: 'Actual Burst Duration P2 (μs)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 60,
    }),
    columnHelper.accessor('frame_duration', {
      id: 'frame_duration',
      header: 'Frame Duration (μs)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 80,
    }),
    columnHelper.accessor('e1t1_latency', {
      id: 'e1t1_latency',
      header: 'E1/T1 1-way latency (ms)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 60,
    }),
    columnHelper.accessor('phase_1_end', {
      id: 'phase_1_end',
      header: 'Phase 1 End',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      size: 110,
    }),
    columnHelper.accessor('phase_2_end', {
      id: 'phase_2_end',
      header: 'Phase 2 End',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      size: 110,
    }),
    columnHelper.accessor('slave_rx_tx_gap', {
      id: 'slave_rx_tx_gap',
      header: 'Slave RX-TX Gap (μs)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 60,
    }),
    columnHelper.accessor('tdd_frame_offset', {
      id: 'tdd_frame_offset',
      header: 'TDD Frame Offset (μs)',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 60,
    }),
    columnHelper.accessor('symmetry', {
      id: 'symmetry',
      header: 'Symmetry',
      sortingFn: 'alphanumeric',
      aggregationFn: 'firstChild',
      meta: { tdStyle: { textAlign: 'right' } },
      size: 65,
    }),
  ] as ColumnDef<TddSyncLink>[];
};
